import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { coursesAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { GraduationCap, Plus, Edit, Trash2, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const emptyForm = { title: '', description: '', category: '', image_url: '' };
const emptyLesson = { title: '', content: '', video_url: '' };

export default function CoursesPage() {
  const { selectedChurchId, canCreate, canUpdate, canDelete } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [lessonsOf, setLessonsOf] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonForm, setLessonForm] = useState(emptyLesson);
  const [total, setTotal] = useState(0);
  const pg = usePagination(String(selectedChurchId));

  const fetchItems = useCallback(() => {
    setLoading(true);
    coursesAPI.list(pg.params)
      .then(res => { setItems(res.data?.data || []); setTotal(res.data?.total || 0); })
      .catch(err => toast.error(hubError(err, 'Error al cargar cursos')))
      .finally(() => setLoading(false));
  }, [pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      title: item.title || '', description: item.description || '',
      category: item.category || '', image_url: item.image_url || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Título requerido'); return; }
    setSaving(true);
    const payload = { ...form, category: form.category || null, image_url: form.image_url || null };
    try {
      if (editing) { await coursesAPI.update(editing, payload); toast.success('Curso actualizado'); }
      else { await coursesAPI.create(payload); toast.success('Curso creado'); }
      setDialogOpen(false); fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al guardar')); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await coursesAPI.delete(id); toast.success('Curso eliminado'); fetchItems(); }
    catch (err) { toast.error(hubError(err, 'Error al eliminar')); }
  };

  const openLessons = (item) => {
    setLessonsOf(item);
    setLessonForm(emptyLesson);
    setLessonsLoading(true);
    coursesAPI.lessons(item.id)
      .then(res => setLessons(res.data?.data || []))
      .catch(err => toast.error(hubError(err, 'Error al cargar lecciones')))
      .finally(() => setLessonsLoading(false));
  };

  const addLesson = async () => {
    if (!lessonForm.title) { toast.error('Título de la lección requerido'); return; }
    try {
      await coursesAPI.createLesson(lessonsOf.id, { ...lessonForm, video_url: lessonForm.video_url || null });
      toast.success('Lección agregada');
      setLessonForm(emptyLesson);
      openLessons(lessonsOf);
    } catch (err) { toast.error(hubError(err, 'Error al agregar lección')); }
  };

  const deleteLesson = async (lessonId) => {
    try {
      await coursesAPI.deleteLesson(lessonId);
      setLessons(prev => prev.filter(l => l.id !== lessonId));
      toast.success('Lección eliminada');
    } catch (err) { toast.error(hubError(err, 'Error al eliminar lección')); }
  };

  return (
    <div>
      <TopBar title="Cursos" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={canCreate('courses') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={openCreate} data-testid="courses-create-button"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="cursos" testId="courses-pagination"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><GraduationCap className="w-12 h-12 text-muted-foreground/40" /><p>No hay cursos</p>{canCreate('courses') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={openCreate}>Crear curso</Button>}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <Card key={item.id} className="stat-card overflow-hidden">
                {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-32 object-cover" />}
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#2B7DBA]/10 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-[#2B7DBA]" />
                    </div>
                    <div className="flex gap-1">
                      {canUpdate('courses') && <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`course-edit-${item.id}`}><Edit className="w-4 h-4" /></Button>}
                      {canDelete('courses') && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`course-delete-${item.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>¿Eliminar curso?</AlertDialogTitle><AlertDialogDescription>No se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                  {item.category && <Badge variant="secondary" className="text-[10px] mb-2">{item.category}</Badge>}
                  {item.description && <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>}
                  <button onClick={() => openLessons(item)} className="flex items-center gap-1 text-xs text-[#056A84] hover:underline mt-3" data-testid={`course-lessons-${item.id}`}>
                    <BookOpen className="w-3 h-3" /> Ver lecciones
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nuevo'} Curso</DialogTitle><DialogDescription className="sr-only">Formulario de curso</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Título *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="course-title-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="course-description-input" /></div>
            <div className="space-y-2"><Label>Categoría</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="course-category-input" /></div>
            <div className="space-y-2"><Label>Imagen (URL)</Label><Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="course-save-button">{saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}{editing ? 'Actualizar' : 'Crear'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lessonsOf} onOpenChange={() => setLessonsOf(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lecciones · {lessonsOf?.title}</DialogTitle>
            <DialogDescription>Gestiona el contenido del curso</DialogDescription>
          </DialogHeader>
          {lessonsLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 spinner text-[#056A84]" /></div>
          ) : (
            <div className="space-y-2" data-testid="course-lessons-list">
              {lessons.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Este curso aún no tiene lecciones</p>}
              {lessons.map((l, i) => (
                <div key={l.id} className="flex items-start justify-between gap-2 p-3 rounded-lg border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{i + 1}. {l.title}</p>
                    {l.content && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{l.content}</p>}
                  </div>
                  {canDelete('courses') && (
                    <Button variant="ghost" size="sm" onClick={() => deleteLesson(l.id)} data-testid={`lesson-delete-${l.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {canCreate('courses') && (
            <div className="border-t pt-4 space-y-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Nueva lección</p>
              <Input value={lessonForm.title} onChange={e => setLessonForm({...lessonForm, title: e.target.value})} placeholder="Título" data-testid="lesson-title-input" />
              <Textarea value={lessonForm.content} onChange={e => setLessonForm({...lessonForm, content: e.target.value})} rows={3} placeholder="Contenido" data-testid="lesson-content-input" />
              <Input value={lessonForm.video_url} onChange={e => setLessonForm({...lessonForm, video_url: e.target.value})} placeholder="Video (URL)" data-testid="lesson-video-input" />
              <Button className="bg-[#056A84] hover:bg-[#045A70] text-white w-full" onClick={addLesson} data-testid="lesson-save-button">
                <Plus className="w-4 h-4 mr-1" /> Agregar lección
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
